import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchStats } from "../services/statsService";
import { StatsCounterItem } from "../types/stats";
import { fetchPilotGauges as fetchPilotGaugesApi } from "../services/gaugesService";

export interface PilotGaugeData {
  gaugeId: string;
  value: number;
}

export interface PilotStatsData {
  totalKills: StatsCounterItem | null;
  killsByAirframe: StatsCounterItem[];
  killsByWeaponFamily: StatsCounterItem[];
  killsByVictimDomainAndCategory: StatsCounterItem[];

  totalDuration: StatsCounterItem | null;
  durationByAirframe: StatsCounterItem[];

  totalLandings: StatsCounterItem | null;
  carrierLandings: StatsCounterItem | null;
  landingsByType: StatsCounterItem[];

  gauges: PilotGaugeData[];
}

export interface UsePilotStatsResult {
  data: PilotStatsData | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const DEFAULT_LIMIT = 10;
const VICTIM_DOMAIN_CATEGORY_LIMIT = 100;

interface UsePilotStatsOptions {
  gaugeIds?: string[];
}

export default function usePilotStats(pilotUid: string | null, options: UsePilotStatsOptions = {}): UsePilotStatsResult {
  const [data, setData] = useState<PilotStatsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pilotUidRef = useRef<string | null>(pilotUid);
  pilotUidRef.current = pilotUid;

  const gaugeIdsRef = useRef<string[]>(options.gaugeIds ?? []);
  gaugeIdsRef.current = options.gaugeIds ?? [];

  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    const uid = pilotUidRef.current;
    if (!uid) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;

    setLoading(true);
    setError(null);

    try {
      const [
        totalKills,
        killsByAirframe,
        killsByWeaponFamily,
        killsByVictimDomainAndCategory,
        totalDuration,
        durationByAirframe,
        totalLandings,
        carrierLandings,
        landingsByType,
        gauges,
      ] = await Promise.all([
        fetchStats({ pilot_uid: uid, schema_name: "TOTAL_KILLS", limit: 1 }),
        fetchStats({ pilot_uid: uid, schema_name: "KILLS_BY_AIRFRAME", order: "count", direction: "desc", limit: DEFAULT_LIMIT }),
        fetchStats({ pilot_uid: uid, schema_name: "KILLS_BY_WEAPON_FAMILY", order: "count", direction: "desc", limit: DEFAULT_LIMIT }),
        fetchStats({
          pilot_uid: uid,
          schema_name: "KILLS_BY_VICTIM_DOMAIN_AND_CATEGORY",
          order: "count",
          direction: "desc",
          limit: VICTIM_DOMAIN_CATEGORY_LIMIT,
        }),
        fetchStats({ pilot_uid: uid, schema_name: "TOTAL_DURATION", limit: 1 }),
        fetchStats({ pilot_uid: uid, schema_name: "DURATION_BY_AIRFRAME", order: "value_total", direction: "desc", limit: DEFAULT_LIMIT }),
        fetchStats({ pilot_uid: uid, schema_name: "LANDINGS", limit: 1 }),
        fetchStats({ pilot_uid: uid, schema_name: "CARRIER_LANDINGS", limit: 1 }),
        fetchStats({ pilot_uid: uid, schema_name: "LANDINGS_BY_TYPE", order: "count", direction: "desc", limit: DEFAULT_LIMIT }),
        fetchPilotGauges(uid, gaugeIdsRef.current),
      ]);

      if (requestId !== requestIdRef.current) {
        return;
      }

      setData({
        totalKills: totalKills.items[0] ?? null,
        killsByAirframe: killsByAirframe.items,
        killsByWeaponFamily: killsByWeaponFamily.items,
        killsByVictimDomainAndCategory: killsByVictimDomainAndCategory.items,

        totalDuration: totalDuration.items[0] ?? null,
        durationByAirframe: durationByAirframe.items,

        totalLandings: totalLandings.items[0] ?? null,
        carrierLandings: carrierLandings.items[0] ?? null,
        landingsByType: landingsByType.items,

        gauges,
      });
    } catch (err) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      const message = err instanceof Error ? err.message : "Unexpected error";
      setError(message);
      setData(null);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, pilotUid]);

  const refresh = useCallback(() => {
    void load();
  }, [load]);

  return useMemo(() => ({ data, loading, error, refresh }), [data, error, loading, refresh]);
}

async function fetchPilotGauges(pilotUid: string, gaugeIds: string[]): Promise<PilotGaugeData[]> {
  if (!gaugeIds.length) return [];

  const response = await fetchPilotGaugesApi(pilotUid);
  const gauges = response?.gauges && typeof response.gauges === "object" ? response.gauges : {};

  return gaugeIds
    .map((gaugeId) => {
      const value = gauges[gaugeId]?.value;
      if (typeof value !== "number" || !Number.isFinite(value)) return null;
      return { gaugeId, value };
    })
    .filter((entry): entry is PilotGaugeData => entry !== null);
}
